/**
 * reef-mobile-nav.js — Reef Lagoon custom mobile navigation drawer.
 *
 * REPLACES Horizon's native <details> mobile drawer, whose native `open` state
 * desyncs from the JS/CSS state on iOS and strands <html scroll-lock> (page
 * freeze). This drawer:
 *   - owns its state via an `.is-open` class — never the native <details>;
 *   - uses an iOS-safe body-`position:fixed` scroll lock (the document root is
 *     the scroller <990px), which is the ONLY lock it engages;
 *   - is portaled to <body> so `position:fixed` is viewport-relative (the header
 *     carries backdrop-filter + `contain:layout`, making it the containing block
 *     for fixed descendants otherwise);
 *   - neutralises the native drawer by removing the trigger's delegated
 *     `on:click` and the <details>' `scroll-lock` attribute, so Horizon never
 *     opens it and dialog.js never locks scroll for it.
 *
 * Progressive enhancement: the native drawer is only hidden (via CSS keyed on
 * `html.reef-mnav-ready`) once this script has adopted the trigger — so if the
 * script fails to load, the native drawer still works. No base JS files edited.
 */
(function () {
  if (window.__reefMobileNav) return;
  window.__reefMobileNav = true;

  var DETAILS_ID = 'Details-menu-drawer-container';
  var OPEN = 'is-open';

  var lockedY = 0;
  var scrollLocked = false;
  var lastFocus = null;

  function nav() {
    return document.querySelector('[data-reef-mnav]');
  }
  function details() {
    return document.getElementById(DETAILS_ID);
  }
  function trigger() {
    var d = details();
    return d ? d.querySelector('summary') : null;
  }
  function panel() {
    var n = nav();
    return n ? n.querySelector('[data-reef-mnav-panel]') : null;
  }
  function isOpen() {
    var n = nav();
    return !!n && n.classList.contains(OPEN);
  }

  /* ---------- iOS-safe scroll lock (document root is the scroller <990px) ---------- */
  function lockScroll() {
    if (scrollLocked) return;
    lockedY = window.scrollY || document.documentElement.scrollTop || 0;
    var b = document.body;
    b.style.position = 'fixed';
    b.style.top = -lockedY + 'px';
    b.style.left = '0';
    b.style.right = '0';
    b.style.width = '100%';
    scrollLocked = true;
  }
  function unlockScroll() {
    if (!scrollLocked) return;
    var b = document.body;
    b.style.position = '';
    b.style.top = '';
    b.style.left = '';
    b.style.right = '';
    b.style.width = '';
    scrollLocked = false;
    window.scrollTo(0, lockedY);
  }

  /* ---------- focus management ---------- */
  function focusables() {
    var p = panel();
    if (!p) return [];
    var list = p.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])');
    return Array.prototype.filter.call(list, function (el) {
      // Skip anything inside a collapsed (hidden) accordion.
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
    });
  }

  /* ---------- open / close ---------- */
  function open() {
    var n = nav();
    if (!n || isOpen()) return;
    lastFocus = document.activeElement;
    n.hidden = false;
    void n.getBoundingClientRect(); // reflow so the open transition runs
    n.classList.add(OPEN);
    lockScroll();
    var t = trigger();
    if (t) t.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', onKeydown, true);
    var focusTarget = n.querySelector('[data-reef-mnav-close]') || focusables()[0];
    if (focusTarget) focusTarget.focus();
  }

  function close() {
    var n = nav();
    if (!n || !isOpen()) return;
    n.classList.remove(OPEN);
    unlockScroll();
    var t = trigger();
    if (t) t.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', onKeydown, true);

    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hide = function () {
      if (!isOpen()) n.hidden = true;
    };
    if (reduce) hide();
    else window.setTimeout(hide, 320);

    // Return focus to the hamburger.
    var back = lastFocus && document.contains(lastFocus) ? lastFocus : t;
    if (back && typeof back.focus === 'function') back.focus();
  }

  /* ---------- handlers ---------- */
  function onTriggerClick(e) {
    // Pre-empt the native <details> toggle AND Horizon's delegated on:click.
    e.preventDefault();
    e.stopImmediatePropagation();
    open();
  }

  function onNavClick(e) {
    if (e.target.closest('[data-reef-mnav-close]')) {
      e.preventDefault();
      close();
      return;
    }
    if (e.target.closest('[data-reef-mnav-backdrop]')) {
      close();
      return;
    }
    // A real navigation link: release the lock before the page changes.
    if (e.target.closest('a[href]')) close();
  }

  // Shared close for the X and backdrop, bound DIRECTLY to each element. Runs the
  // exact same close() the freeze fix uses (restores body position:fixed + scroll).
  // stopPropagation prevents the delegated onNavClick from also firing (close() is
  // idempotent anyway).
  function onCloseControl(e) {
    e.preventDefault();
    e.stopPropagation();
    close();
  }

  // Bulletproof close: ONE listener on `document` catches a [data-reef-mnav-close]
  // tap in ANY drawer instance. This matters because Horizon's section hydration
  // re-renders the header AFTER we portal, leaving a duplicate drawer inside the
  // header (backdrop-filter/contain) as the visible one — per-instance listeners
  // land on the wrong copy. The close controls are native <a href="#">, so the tap
  // is delivered by the browser; we just preventDefault the "#" and run the shared
  // freeze-safe unlock. open() and close() both target nav() (the first instance),
  // so they always stay in sync — close() handles the open drawer + body unlock.
  function onDocClose(e) {
    var t = e.target.closest && e.target.closest('[data-reef-mnav-close]');
    if (!t) return;
    e.preventDefault();
    close();
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var f = focusables();
    if (!f.length) return;
    var first = f[0];
    var last = f[f.length - 1];
    var active = document.activeElement;
    var p = panel();
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    } else if (p && !p.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- wiring / neutralising the native drawer ---------- */
  function wire() {
    var n = nav();
    var t = trigger();
    if (!n || !t) return false;

    // Portal to <body> so position:fixed is viewport-relative.
    if (n.parentElement !== document.body) document.body.appendChild(n);

    if (!n.dataset.reefBound) {
      n.dataset.reefBound = '1';
      n.hidden = true;
      // Delegated fallback (belt-and-braces).
      n.addEventListener('click', onNavClick);
      // Primary, RELIABLE close bindings: bound DIRECTLY to each element, because
      // iOS Safari does not reliably deliver a bubbled `click` to a delegated
      // ancestor for taps on non-anchor nodes (the <div> backdrop, the aria-hidden
      // <svg> inside the button). Links keep working because their navigation is
      // native. Both controls move WITH the portaled drawer, so these stay bound.
      var closeBtn = n.querySelector('[data-reef-mnav-close]');
      if (closeBtn) closeBtn.addEventListener('click', onCloseControl);
      var backdrop = n.querySelector('[data-reef-mnav-backdrop]');
      if (backdrop) backdrop.addEventListener('click', onCloseControl);
    }

    // Disable the base scroll-lock path for the native drawer entirely.
    var d = details();
    if (d) d.removeAttribute('scroll-lock');

    if (!t.dataset.reefBound) {
      t.dataset.reefBound = '1';
      t.removeAttribute('on:click'); // detaches Horizon's live-delegated toggle()
      t.setAttribute('aria-haspopup', 'dialog');
      t.setAttribute('aria-expanded', 'false');
      t.addEventListener('click', onTriggerClick, true); // capture: beat the default
    }

    // Signal CSS to hide the (now inert) native drawer. Only set once we're wired,
    // so the native drawer still works if this script never runs.
    document.documentElement.classList.add('reef-mnav-ready');
    return true;
  }

  function init() {
    // Primary close path: one delegated listener on document, reliable across every
    // drawer instance and inside the header's backdrop-filter/contain context.
    document.addEventListener('click', onDocClose);

    if (!wire()) {
      var mo = new MutationObserver(function () {
        if (wire()) mo.disconnect();
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    }

    // Theme editor: the header re-renders on section load. Drop any stale portaled
    // copy and re-adopt the fresh render.
    document.addEventListener('shopify:section:load', function () {
      var copies = document.querySelectorAll('body > [data-reef-mnav]');
      copies.forEach(function (c) {
        if (!c.classList.contains(OPEN)) c.remove();
      });
      wire();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
